/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/main.tsx
 * DESCRIPTION: Application entry point with Clerk authentication,
 *              Organizations support, Admin authentication, Workspace
 *              scope context, and NEW API client initialization for
 *              Swagger v2.0.0.
 *
 * PROVIDER HIERARCHY:
 *   ClerkProvider          → Auth & Organizations (must be outermost)
 *   └── WorkspaceProvider  → Current scope: global | personal | org
 *       └── AdminAuthProvider → Admin password gating
 *           └── App
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.2.0
 * @created 2025-01-15
 * @updated 2026-04-08 - Updated to use new apiClient.ts with
 *                       VITE_VERIMARK_* environment variables
 * @updated 2026-08-24 - Added comprehensive dark mode support for
 *                       Clerk components, improved appearance config
 * @updated 2026-09-05 - Added WorkspaceProvider for account data
 *                       segregation (Phase 1: Personal Workspace)
 * ─────────────────────────────────────────────────────────────────────
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';

// Clerk imports
import { ClerkProvider } from '@clerk/react';

// Admin Auth Context
import { AdminAuthProvider } from './contexts/AdminAuthContext';

// Workspace Scope Context (Phase 1: Data Segregation)
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { NotificationProvider } from './contexts/NotificationContext';


// NEW API Client & Auth Service (Swagger v2.0.0)
import { ClerkAuthService } from './lib/auth/ClerkAuthService';
import { initializeApiClient } from './lib/api/apiClient';

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key in environment variables");
}

// ─────────────────────────────────────────────────────────────────────
// Dark Mode Initialization
// ─────────────────────────────────────────────────────────────────────
const initializeDarkMode = () => {
  const savedDarkMode = localStorage.getItem('darkMode');

  if (savedDarkMode !== null) {
    if (JSON.parse(savedDarkMode)) {
      document.documentElement.classList.add('dark');
    }
  } else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    }
  }
};

initializeDarkMode();

// ─────────────────────────────────────────────────────────────────────
// Initialize Auth Service & NEW API Client
// Uses VITE_VERIMARK_* environment variables
// ─────────────────────────────────────────────────────────────────────
const authService = new ClerkAuthService();

const apiClient = initializeApiClient(
  import.meta.env.VITE_VERIMARK_API_BASE,
  import.meta.env.VITE_VERIMARK_API_KEY,
  authService
);

// ─────────────────────────────────────────────────────────────────────
// Render App
// ─────────────────────────────────────────────────────────────────────
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      appearance={{
        baseTheme: undefined, // Allow inheriting from app's dark mode
        variables: {
          colorPrimary: '#0ea5e9',
          colorBackground: 'transparent', // Let parent container control background
          colorInputBackground: 'var(--bg-gray-700, #f9fafb)',
          colorInputText: 'var(--text-gray-900, #111827)',
          colorText: 'var(--text-gray-900, #111827)',
          colorTextSecondary: 'var(--text-gray-600, #6b7280)',
          colorBorder: 'var(--border-gray-300, #d1d5db)',
        },
        elements: {
          // Root container
          rootBox: 'w-full dark:bg-gray-800',

          // Card styling
          card: 'bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700',

          // Headers
          headerTitle: 'text-xl font-bold text-gray-900 dark:text-white',
          headerSubtitle: 'text-gray-600 dark:text-gray-400',

          // Form elements
          formButtonPrimary: 'bg-primary-600 hover:bg-primary-700 text-white dark:bg-blue-600 dark:hover:bg-blue-700',
          formFieldInput: 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-blue-500',
          formFieldLabel: 'text-gray-700 dark:text-gray-300',
          formFieldAction: 'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300',

          // Social buttons
          socialButtonsBlockButton: 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300',

          // Profile sections
          profileSectionTitle: 'text-lg font-semibold text-gray-900 dark:text-white',
          profileSectionContent: 'space-y-4',

          // Navigation
          navbar: 'bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700',
          navbarButton: 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white',
          navbarButtonActive: 'text-blue-600 dark:text-blue-400',

          // User button popover
          userButtonPopoverCard: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
          userButtonPopoverActionButton: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',

          // Alerts and messages
          alert: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400',
          alertError: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400',
          alertSuccess: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400',
        }
      }}
    >
      {/* Phase 1: Workspace scope context for data segregation */}
     <WorkspaceProvider>
      <AdminAuthProvider>
        <NotificationProvider>
          <App />
          </NotificationProvider>
        </AdminAuthProvider>
      </WorkspaceProvider>
    </ClerkProvider>
  </React.StrictMode>,
);